import isArray from 'lodash/isArray';
import { isPristine, isInvalid } from 'redux-form';

import {
    CTA_COMPLETE_FREE_TEXT,
    CTA_COMPLETE_PAID_TEXT,
    CTA_CHECKOUT_TEXT,
    CTA_PICK_A_SEAT_TEXT,
    CTA_JOIN_WAITLIST,
} from '../constants';
import {
    allTicketsFree,
    hasEnteredDonationAmount,
} from '../containers/ticketSelection/utils/ticketUtils';
import { getSelectedTickets } from '../selectors/tickets';

// XXX: isSubmitting selector is introduced on redux-form@6.4.0 and we are on redux-form@6.2.0
// so I adapted the code from the latest version of the lib to have the same behavior.
// Once we upgrade the lib, we can get rid of this code.
// https://github.com/erikras/redux-form/blob/v6.4.0/src/selectors/isSubmitting.js
export const isSubmitting = (formName) => (state) => {
    const formState = state.form && state.form[formName];

    return (formState && formState.submitting) || false;
};

// XXX: Same as isSubmitting selector above; introduced in redux-form@6.4.0
const hasSubmitSucceeded = (formName) => (state) => {
    const formState = state.form && state.form[formName];

    return (formState && formState.submitSucceeded) || false;
};

export const hasFormSubmitted = (state, formName, disableOnSucceeded = true) =>
    isSubmitting(formName)(state) ||
    (disableOnSucceeded && hasSubmitSucceeded(formName)(state));

export const isSubmitDisabled = (state, formName, disableOnSucceeded) =>
    isPristine(formName)(state) ||
    hasFormSubmitted(state, formName, disableOnSucceeded) ||
    isInvalid(formName)(state);

/**
 * TODO: Move to selectors/event.js or tickets.js
 * CTA text for the ticket selection screen. On initial widget load, if there
 * are no selected tickets, we base the CTA text off of the available tickets.
 * @param  {object} app         The app state
 * @return {string}             CTA text
 */
// TODO: Move selector to `/selectors/event.js`
export const getTicketSelectDefaultCtaText = ({
    app: { featureFlags: { enablePickASeat } = {} } = {},
    event: { isFree },
}) => {
    if (enablePickASeat) {
        return CTA_PICK_A_SEAT_TEXT;
    }

    if (isFree) {
        return CTA_COMPLETE_FREE_TEXT;
    }

    return CTA_CHECKOUT_TEXT;
};

/**
 * Returns the CTA text for the special ticket selection page when the event capacity
 * waitlist has been triggered.
 * @param {object} state
 * TODO: Move to `selectors/tickets.js`
 */
// TODO: Move selector to `/selectors/tickets.js`, use getTicketsById
export const getEventCapacityWaitlistTicketsPageCtaText = (state) => {
    const {
        tickets: { ticketsById },
    } = state;

    if (hasEnteredDonationAmount(ticketsById)) {
        return CTA_CHECKOUT_TEXT;
    }

    return CTA_JOIN_WAITLIST;
};

// TODO: Move selector to `/selectors/tickets.js`
export const getCheckoutDefaultCtaText = (state) => {
    const selectedTickets = getSelectedTickets(state);
    const showFreeCta = allTicketsFree(selectedTickets);

    if (showFreeCta) {
        return CTA_COMPLETE_FREE_TEXT;
    }

    return CTA_COMPLETE_PAID_TEXT;
};

/**
 * Get common form props used by all forms in the checkoutWidget app
 *
 * @param  {object} state              app state
 * @param  {string} formName           name of form
 * @return {object} props with cta text to show and whether submit is disabled
 */
// TODO: Move to /selectors/?, separate into two selectors
// Two places where this is used consume both props, everywhere else consumes one
export const getFormButtonProps = (
    state,
    formName,
    disableOnSucceeded = true,
) => ({
    isSubmitting: hasFormSubmitted(state, formName, disableOnSucceeded),
    isSubmitDisabled: isSubmitDisabled(state, formName, disableOnSucceeded),
});

export const flattenFormData = (data) => {
    const formData = isArray(data) ? [...data] : { ...data };

    for (const key in formData) {
        if (Object.prototype.hasOwnProperty.call(formData, key)) {
            let value = formData[key];

            if (
                typeof value !== 'object' ||
                value === null ||
                !formData.hasOwnProperty(key) ||
                isArray(value)
            ) {
                continue;
            }

            // for multi-level flattening
            value = flattenFormData(value);

            for (const nestedKey in value) {
                if (!value.hasOwnProperty(nestedKey)) {
                    continue;
                }

                formData[`${key}.${nestedKey}`] = value[nestedKey];
            }

            delete formData[key];
        }
    }

    return formData;
};
